import { AnimatedScrollView } from '@kanelloc/react-native-animated-header-scroll-view';
import { useFeatureGate } from '@statsig/expo-bindings';
import { useSetAtom } from 'jotai';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { DimensionValue, LayoutChangeEvent } from 'react-native';
import type Animated from 'react-native-reanimated';
import { useAnimatedRef } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    isNonMobileWeb,
    isWeb,
    PageBelowHeader,
    useMedia,
    View,
    XStack,
    YStack,
} from 'smoothie';

import BaseHomeTab from '../../../test/pages/home/HomeTab';
import BankingExperienceToggle from '../../banking/components/BankingExperienceToggle';
import { BankingSwitch } from '../../bankingSwitch/components/BankingSwitch';
import { BackgroundGradient } from '../../common/components/BackgroundGradient';
import { BASE_TAB_HEADER_HEIGHT } from '../../common/components/TabHeader';
import { StorageKeys } from '../../common/constants';
import { useStorage } from '../../common/providers/StorageProvider';
import { useTrackViewOnFocus } from '../../events/hooks/useTrackViewOnFocus';
import { FeatureFlags } from '../../feature-flags/FeatureFlags';
import { useNextGuidanceSessionOnHomeDisplay } from '../../guidanceSessions/hooks/useNextGuidanceSessionOnHomeDisplay';
import { ReferralsCtaCard } from '../../referrals/components/ReferralsCtaCard';
import { HomeMoneyMapSection } from '../../roadmap/components/RoadmapMoneyMapSection';
import { FreeToSpendPeriodAtom } from '../state/FreeToSpendAtoms';
import { ActivateCardWidget } from './activate-card/ActivateCardWidget';
import BillsOnHome from './bills/BillsOnHome';
import { EmploymentPrompt } from './employment/EmploymentPrompt';
import { AccountBalancesCarousel } from './free-to-spend/AccountBalancesCarousel';
import SpendingSummary from './free-to-spend/SpendingSummary';
import HomeGoalTile from './goal-tile/HomeGoalTile';
import { MoneyMapProjectionCard } from './goal-tile/MoneyMapProjectionCard';
import GuidanceGoalsTasksTile from './guidance/GuidanceGoalsTasksTile';
import NextGuidanceSessionWidget from './guidance/NextGuidanceSessionWidget';
import {
    HomeHeader,
    HomeHeaderNavbar,
    HomeTopNavbar,
} from './header/HomeHeader';
import PendingJointInvitationsNotice from './joint-invitations/PendingJointInvitationsNotice';
import OnboardingSheet from './onboarding/OnboardingSheet';
import { OnboardingWidget } from './onboarding/OnboardingWidget';
import { ProductTesting } from './ProductTesting';
import { SproutCtaCard } from './sprout/SproutCtaCard';
import YourMoney from './your-money/YourMoney';

const MAX_WIDTH_CONTENT = 800;
const MAX_WIDTH_FOOTER = 1400;
const DEFAULT_GAP = 32;
const DEFAULT_GAP_SM = 24;
const DEFAULT_GAP_LG = 48;
const SPACING_SM = 24;
const SPACING_MD = 48;
const SPACING_LG = 64;

const HomeHeadsUpSection = ({
    showSessionWidget = true,
    showProjectionCard = true,
}: {
    showSessionWidget?: boolean;
    showProjectionCard?: boolean;
}) => {
    const { value: freeToSpendWidgetEnabled } = useFeatureGate(
        FeatureFlags.ENABLE_FREE_TO_SPEND_WIDGET,
    );
    const { value: goyoEnabled } = useFeatureGate(FeatureFlags.ENABLE_GOYO);

    return (
        <>
            <PendingJointInvitationsNotice />
            <ActivateCardWidget />
            {showSessionWidget && <NextGuidanceSessionWidget />}
            {showProjectionCard && <MoneyMapProjectionCard />}
            <OnboardingWidget />
            <HomeGoalTile />
            <HomeMoneyMapSection />
            <BankingSwitch layout="widget" />
            {freeToSpendWidgetEnabled && <SpendingSummary />}
            <SproutCtaCard />
            {!goyoEnabled && <GuidanceGoalsTasksTile />}
            <View display="none" $md={{ display: 'flex' }}>
                <ReferralsCtaCard />
            </View>
        </>
    );
};

const Home = () => {
    useTrackViewOnFocus('Home');
    const insets = useSafeAreaInsets();
    const scrollRef = useAnimatedRef<Animated.ScrollView>();
    const breakpoints = useMedia();

    const dynamicCardFlexBasis = isWeb
        ? (`calc(50% - ${DEFAULT_GAP / 2}px)` as unknown as DimensionValue)
        : '100%';

    const { value: freeToSpendWidgetEnabled } = useFeatureGate(
        FeatureFlags.ENABLE_FREE_TO_SPEND_WIDGET,
    );
    const { value: goyoEnabled } = useFeatureGate(FeatureFlags.ENABLE_GOYO);
    const {
        shouldShowHeroSessionWidget,
        shouldShowHeroGuidanceSessionWidget,
        shouldShowHeroBookingWidget,
        shouldShowHeadsUpBookingWidget,
        heroFlexDirection,
    } = useNextGuidanceSessionOnHomeDisplay();

    // Under GOYO the guide *booking* widget never belongs in the hero — only an
    // actual upcoming session does. Any booking prompt is moved down to the
    // heads-up (below) section instead.
    let heroSessionWidgetVisible = shouldShowHeroSessionWidget;
    let headsUpSessionWidgetVisible = shouldShowHeadsUpBookingWidget;
    if (goyoEnabled) {
        heroSessionWidgetVisible = shouldShowHeroGuidanceSessionWidget;
        headsUpSessionWidgetVisible =
            shouldShowHeadsUpBookingWidget || shouldShowHeroBookingWidget;
    }

    const storage = useStorage();
    const setSpendPeriod = useSetAtom(FreeToSpendPeriodAtom);

    useEffect(() => {
        const stored = storage.getString(StorageKeys.SPEND_WIDGET_PERIOD);
        if (stored === 'weekly' || stored === 'monthly') {
            setSpendPeriod(stored);
        }
    }, [storage]);

    const [shouldHaveSidebarGap, setShouldHaveSidebarGap] = useState(false);
    const onSidebarLayout = useCallback((e: LayoutChangeEvent) => {
        if (e.nativeEvent.layout.width > 0) {
            setShouldHaveSidebarGap(true);
        }
    }, []);

    const topBarHeight = BASE_TAB_HEADER_HEIGHT + insets.top;

    return (
        <View flex={1} testID={BaseHomeTab.pageId}>
            <BackgroundGradient />
            <AnimatedScrollView
                style={{ flex: 1 }}
                isHeaderComponentFixed={true}
                isFullScreen={true}
                contentContainerStyle={{
                    flexGrow: 1,
                }}
                showsVerticalScrollIndicator={isWeb}
                alwaysBounceVertical={false}
                topBarHeight={topBarHeight}
                headerMaxHeight={topBarHeight}
                HeaderComponent={<HomeHeader />}
                HeaderNavbarComponent={<HomeHeaderNavbar />} // rendered at the top of home.
                TopNavBarComponent={<HomeTopNavbar />} // rendered after scrolling down on home.
                ref={scrollRef}
                scrollEventThrottle={16}
            >
                <YStack flexDirection={heroFlexDirection}>
                    {heroSessionWidgetVisible && (
                        <YStack
                            paddingHorizontal={24}
                            paddingBottom={goyoEnabled ? 10 : 24}
                            $md={{ display: 'none' }}
                        >
                            <NextGuidanceSessionWidget />
                        </YStack>
                    )}
                    {goyoEnabled && (
                        <YStack
                            paddingHorizontal={24}
                            paddingBottom={24}
                            $md={{ display: 'none' }}
                        >
                            <MoneyMapProjectionCard />
                        </YStack>
                    )}
                    {(freeToSpendWidgetEnabled || goyoEnabled) &&
                        !isNonMobileWeb && (
                            <AccountBalancesCarousel
                                containerProps={{
                                    marginBottom: 24,
                                }}
                            />
                        )}
                </YStack>
                <PageBelowHeader
                    padding={SPACING_SM}
                    $sm={{
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                    }}
                    $md={{
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        padding: SPACING_MD,
                    }}
                    $lg={{
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        padding: SPACING_LG,
                    }}
                >
                    <>
                        <XStack
                            width="100%"
                            flexDirection="column"
                            $md={{
                                flexDirection: 'row-reverse',
                                justifyContent: 'center',
                                gap: shouldHaveSidebarGap ? SPACING_MD : 0,
                                animation: 'quicker',
                                animateOnly: ['gap'],
                            }}
                            $lg={{
                                gap: shouldHaveSidebarGap ? SPACING_LG : 0,
                            }}
                        >
                            <YStack
                                display="none"
                                gap={DEFAULT_GAP_SM}
                                alignItems="center"
                                $md={{
                                    display: 'flex',
                                    flex: 0,
                                }}
                                onLayout={onSidebarLayout}
                            >
                                <HomeHeadsUpSection
                                    showProjectionCard={false}
                                />
                            </YStack>
                            <View
                                $md={{
                                    flex: 1,
                                    flexBasis: '66%',
                                    maxWidth: MAX_WIDTH_CONTENT,
                                }}
                            >
                                <YStack
                                    gap={DEFAULT_GAP_SM}
                                    alignItems="center"
                                    width="100%"
                                >
                                    <View
                                        display="none"
                                        width="100%"
                                        $md={{ display: 'flex' }}
                                    >
                                        <MoneyMapProjectionCard backgroundColor="$gray200" />
                                    </View>
                                    {!breakpoints.md && (
                                        <HomeHeadsUpSection
                                            showSessionWidget={
                                                headsUpSessionWidgetVisible
                                            }
                                            showProjectionCard={false}
                                        />
                                    )}

                                    <YStack
                                        gap={DEFAULT_GAP}
                                        minHeight={500}
                                        width="100%"
                                        $lg={{ gap: DEFAULT_GAP_LG }}
                                    >
                                        {(freeToSpendWidgetEnabled ||
                                            goyoEnabled) &&
                                            isNonMobileWeb && (
                                                <AccountBalancesCarousel />
                                            )}
                                        <YourMoney />
                                        <XStack
                                            width="100%"
                                            flexWrap="wrap"
                                            gap={DEFAULT_GAP}
                                            $lg={{ rowGap: DEFAULT_GAP_LG }}
                                        >
                                            <BillsOnHome
                                                flex={1}
                                                flexBasis="100%"
                                                $sm={{
                                                    minWidth: 300,
                                                    flexBasis:
                                                        dynamicCardFlexBasis,
                                                }}
                                            />
                                        </XStack>
                                        <ProductTesting />
                                    </YStack>
                                </YStack>
                            </View>
                        </XStack>
                        <View
                            alignItems="center"
                            paddingVertical={DEFAULT_GAP}
                            $md={{ display: 'none' }}
                        >
                            <ReferralsCtaCard />
                        </View>
                        <YStack
                            gap={16}
                            alignItems="center"
                            alignSelf="center"
                            width="100%"
                            maxWidth={MAX_WIDTH_FOOTER}
                        >
                            <BankingExperienceToggle />
                        </YStack>
                    </>
                </PageBelowHeader>
            </AnimatedScrollView>
            <EmploymentPrompt />
            <OnboardingSheet />
        </View>
    );
};

export default Home;
